/**
 * Phase 4 — Shared UI primitives for NDR / Disputes / Support / Demurrage / Insurance.
 * Additive only — no edits to existing components.
 */
import { useEffect } from "react";
import { X } from "lucide-react";

/* ─── Status pills ─── */
const STATUS_COLORS = {
  // NDR
  OPEN: "bg-red-100 text-red-700 border-red-200",
  ACTION_REQUIRED: "bg-amber-100 text-amber-700 border-amber-200",
  RE_ATTEMPT_SCHEDULED: "bg-blue-100 text-blue-700 border-blue-200",
  RTO_INITIATED: "bg-orange-100 text-orange-700 border-orange-200",
  RESOLVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  CLOSED: "bg-slate-100 text-slate-600 border-slate-200",
  // Disputes
  UNDER_REVIEW: "bg-blue-100 text-blue-700 border-blue-200",
  AWAITING_CUSTOMER: "bg-amber-100 text-amber-700 border-amber-200",
  AWAITING_PARTNER: "bg-amber-100 text-amber-700 border-amber-200",
  RESOLVED_FAVOUR_CUSTOMER: "bg-emerald-100 text-emerald-700 border-emerald-200",
  RESOLVED_FAVOUR_SENDTOO: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ESCALATED: "bg-rose-100 text-rose-700 border-rose-200",
  // Support tickets
  IN_PROGRESS: "bg-blue-100 text-blue-700 border-blue-200",
  // Demurrage
  CALCULATED: "bg-amber-100 text-amber-700 border-amber-200",
  BILLED: "bg-blue-100 text-blue-700 border-blue-200",
  WAIVED: "bg-slate-100 text-slate-600 border-slate-200",
  PAID: "bg-emerald-100 text-emerald-700 border-emerald-200",
  // Insurance
  SUBMITTED: "bg-amber-100 text-amber-700 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REJECTED: "bg-rose-100 text-rose-700 border-rose-200",
  PARTIALLY_APPROVED: "bg-blue-100 text-blue-700 border-blue-200",
};

export function StatusPill({ status, className = "" }) {
  const cls = STATUS_COLORS[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full border ${cls} ${className}`}
      data-testid={`status-pill-${status}`}
    >
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

const PRIORITY_COLORS = {
  URGENT: "bg-red-100 text-red-700 border-red-200",
  HIGH: "bg-amber-100 text-amber-700 border-amber-200",
  MEDIUM: "bg-blue-100 text-blue-700 border-blue-200",
  LOW: "bg-slate-100 text-slate-600 border-slate-200",
};

export function PriorityPill({ priority }) {
  const cls = PRIORITY_COLORS[priority] || PRIORITY_COLORS.MEDIUM;
  return (
    <span
      className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full border ${cls}`}
      data-testid={`priority-pill-${priority}`}
    >
      {priority}
    </span>
  );
}

/* ─── SLA indicator ─── */
export function SlaIndicator({ deadline, breached, resolved }) {
  if (resolved) return <span className="text-[11px] text-slate-400">—</span>;
  if (breached) {
    return (
      <span
        className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full border bg-red-100 text-red-700 border-red-200"
        data-testid="sla-breached"
      >
        BREACHED
      </span>
    );
  }
  if (!deadline) return <span className="text-[11px] text-slate-400">—</span>;

  const dl = new Date(deadline);
  const ms = dl.getTime() - Date.now();
  const hrs = Math.floor(ms / (1000 * 60 * 60));
  const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (ms <= 0) {
    return (
      <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full border bg-red-100 text-red-700 border-red-200">
        OVERDUE
      </span>
    );
  }
  const color =
    hrs < 4 ? "bg-red-50 text-red-700 border-red-200"
      : hrs < 24 ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <span
      className={`inline-flex items-center text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full border ${color}`}
      data-testid="sla-remaining"
    >
      {hrs}h {mins}m
    </span>
  );
}

/* ─── Slide-over panel (right side) ─── */
export function SlideOver({ open, onClose, title, children, width = "max-w-2xl", testId = "slideover" }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    if (open) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid={testId}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className={`absolute top-0 right-0 h-full w-full ${width} bg-white shadow-2xl flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid={`${testId}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}

/* ─── Generic centered modal ─── */
export function Modal({ open, onClose, title, children, width = "max-w-md", testId = "modal" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" data-testid={testId}>
      <div className={`bg-white rounded-xl w-full ${width} shadow-2xl flex flex-col max-h-[90vh]`}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-heading text-base font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid={`${testId}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}

/* ─── Stats row ─── */
export function StatsRow({ items }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="stats-row">
      {items.map((it) => (
        <div
          key={it.label}
          className={`bg-white border border-slate-200 rounded-lg p-3 ${it.highlight ? "ring-2 ring-brand-300" : ""}`}
          data-testid={`stat-${it.key || it.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <div className="text-[10px] uppercase font-bold text-slate-500">{it.label}</div>
          <div className={`text-xl font-bold mt-0.5 ${it.color || "text-slate-800"}`}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── Threaded message bubble ─── */
export function MessageBubble({ msg }) {
  const internal = !!msg.is_internal;
  const align = msg.sender_role === "customer" ? "items-start" : "items-end";
  return (
    <div className={`flex flex-col ${align} mb-3`} data-testid={`msg-${msg.message_id || ""}`}>
      <div className={`max-w-[85%] rounded-lg p-3 text-sm ${internal ? "bg-amber-50 border border-amber-200" : "bg-slate-50 border border-slate-200"}`}>
        {internal && (
          <div className="text-[10px] font-bold text-amber-700 uppercase mb-1">
            Internal — not visible to customer
          </div>
        )}
        <div className="text-[11px] text-slate-500 mb-1">
          <span className="font-semibold capitalize">{msg.sender_role || "user"}</span>
          {" · "}
          {msg.created_at ? new Date(msg.created_at).toLocaleString() : ""}
        </div>
        <div className="text-slate-800 whitespace-pre-wrap">{msg.content}</div>
      </div>
    </div>
  );
}
